import { useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import type { DefaultSelectSingleProps } from '@/components/types';
import { type BaseSelectOption, BaseSelectSingle } from '@/components/BaseSelectSingle';
import { DEBOUNCE_TIME } from '@/std/const/debounceTime';
import { useDebouncedValue } from '@/std/hooks/useDebouncedValue';
import type { ProdutoAdicional } from '../types/ProdutoAdicional';
import {
    apiProdutoAdicionalConsultarQueryOptions,
    type ProdutoAdicionalConsultarParams,
} from '../api/apiProdutoAdicionalConsultar';

export function ProdutoAdicionalSelect({
    allowClear = true,
    autoFocus = false,
    className,
    disabled = false,
    fetchOnMount = false,
    id,
    initialLabel,
    params,
    placeholder,
    ref,
    value,
    onChange,
    onClear,
    onDeselect,
    onSearch,
    onSelect,
}: DefaultSelectSingleProps<string, ProdutoAdicional, ProdutoAdicionalConsultarParams>) {
    const [search, setSearch] = useState('');
    const [searchDebounced] = useDebouncedValue(search, DEBOUNCE_TIME);

    const hasSearch = typeof searchDebounced === 'string' && searchDebounced.length > 0;
    const searchUsingWhere = !fetchOnMount && typeof value === 'string' && !hasSearch;
    const enabled = fetchOnMount || searchUsingWhere || hasSearch;

    const { data, isFetching } = useQuery({
        ...apiProdutoAdicionalConsultarQueryOptions({
            ...params,
            orderby: 'pra_nome',
            pesquisar: searchDebounced,
            qtde_registros: 20,
            registro_inicial: 0,
            where: searchUsingWhere ? `pra_idpk='${value}'` : undefined,
        }),
        enabled,
    });

    const options: BaseSelectOption<string, ProdutoAdicional>[] = useMemo(() => {
        if (!data) {
            if (value) {
                return [
                    {
                        label: initialLabel,
                        obj: null,
                        value,
                    },
                ];
            }

            return [];
        }

        return data.registros.map((obj) => ({
            label: obj.pra_nome,
            obj,
            value: obj.pra_idpk,
        }));
    }, [data, value, initialLabel]);

    const handleSearch = (value: string): void => {
        setSearch(value);
        onSearch?.(value);
    };

    const handleClear = (): void => {
        setSearch('');
        onClear?.();
    };

    return (
        <BaseSelectSingle
            allowClear={allowClear}
            autoFocus={autoFocus}
            className={className}
            disabled={disabled}
            id={id}
            loading={isFetching}
            onChange={onChange}
            onClear={handleClear}
            onDeselect={onDeselect}
            onSelect={onSelect}
            options={options}
            placeholder={placeholder}
            ref={ref}
            showSearch={{ filterOption: false, onSearch: handleSearch }}
            value={value}
        />
    );
}
